import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, PlayCircle, Download, Layers3 } from "lucide-react";
import { coverSrc, fmtXof, CATEGORY_SHORT, COVER_PLACEHOLDER } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useQuote } from "../context/QuoteContext";
import { isSolution } from "./SolutionExtras";
import { useCurrency } from "../context/CurrencyContext";
import { StarRating } from "./StarRating";
import { PromoButton } from "./ProductExtras";
import { Price } from "./Price";
import { EASE } from "./Motion";

const CTA = "flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-[#0369A1] px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#075985]";

export function ProductCard({ product }) {
  const { add } = useCart();
  const { add: addQuote, has: inQuote } = useQuote();
  const { fmt } = useCurrency();
  const isService = product.category === "Prestations de service";
  const isDev = product.category === "Développement informatique";
  const quoteOnly = product.quote_only || (isDev && product.dev_quote_only);
  const solution = isSolution(product);
  const detailTo = product.sales_page || `/produit/${product.product_id}`;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: EASE }}
      data-testid={`product-card-${product.product_id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-lg"
    >
      <Link to={detailTo} className="relative block aspect-[16/10] overflow-hidden bg-slate-100" data-testid={`product-link-${product.product_id}`}>
        <img
          src={coverSrc(product.cover)}
          alt={product.title}
          loading="lazy"
          onError={(e) => { if (e.currentTarget.src !== COVER_PLACEHOLDER) e.currentTarget.src = COVER_PLACEHOLDER; }}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <div className={`pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start gap-2 p-3 ${solution ? "justify-end" : "justify-between"}`} data-testid={`product-badges-${product.product_id}`}>
          {!solution && (
          <span className="min-w-0 truncate rounded-full bg-navy/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm" title={product.category} data-testid={`product-badge-category-${product.product_id}`}>
            {(CATEGORY_SHORT[product.category] || product.category).replace(" proposées", "")}
          </span>
          )}
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-navy backdrop-blur-sm" data-testid={`product-badge-type-${product.product_id}`}>
            {solution ? <><Layers3 size={11} /> Niveau {product.solution_level}</> : product.product_type === "streaming" ? <><PlayCircle size={11} /> Streaming</> : <><Download size={11} /> Téléchargement</>}
          </span>
        </div>
        {product.available_from && new Date(product.available_from) > new Date() && (
          <span className="absolute bottom-3 left-3 z-20 rounded-full bg-amber-400 px-3 py-1 text-[10px] font-bold uppercase text-navy" data-testid={`coming-soon-${product.product_id}`}>
            Bientôt disponible
          </span>
        )}
        {(isService || isDev) && (
          <span className="absolute bottom-3 right-3 z-20 rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid={`service-badge-${product.product_id}`}>
            {isService ? "Service" : "Informatique"}
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <Link to={detailTo}>
          <h3 className="font-display text-sm font-semibold leading-snug text-navy transition-colors duration-200 group-hover:text-ciel">
            {product.title}
          </h3>
        </Link>
        {product.short_description && (
          <p className="line-clamp-2 text-xs leading-relaxed text-slate-500">{product.short_description}</p>
        )}
        <div className="flex items-center gap-2">
          <StarRating value={product.avg_rating} />
          <span className="font-mono text-xs text-slate-400">({product.review_count})</span>
        </div>
        {solution ? (
          <div className="mt-auto flex flex-col gap-3 pt-1" data-testid={`solution-footer-${product.product_id}`}>
            <div className="min-w-0">
              <span className="block text-[10px] font-normal uppercase tracking-wide text-slate-400">Total mensuel</span>
              <span className="block overflow-hidden whitespace-nowrap font-display text-[13px] font-bold leading-tight text-navy" title={product.solution_total_label} data-testid={`product-price-${product.product_id}`}>{product.solution_total_label}</span>
            </div>
            <div className="flex items-center gap-2">
              {product.sales_page ? (
                <Link to={product.sales_page} data-testid={`demo-cta-${product.product_id}`} className={`${CTA} flex-1`}>{product.cta_label || "Demander une démo"}</Link>
              ) : (
                <Link to="/devis" onClick={() => { if (!inQuote(product.product_id)) addQuote(product); }} data-testid={`quote-cta-${product.product_id}`} className={`${CTA} flex-1`}>Demander un devis</Link>
              )}
              <PromoButton product={product} compact />
            </div>
          </div>
        ) : (
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <div className="min-w-0">
            {product.is_free ? (
              <span className="font-display text-base font-bold text-emerald-600" data-testid={`product-price-${product.product_id}`}>Gratuit</span>
            ) : quoteOnly && !product.price ? (
              <span className="whitespace-nowrap font-display text-sm font-bold text-[#0369A1]" data-testid={`product-price-${product.product_id}`}>Sur devis</span>
            ) : (
              <>
                <Price xof={product.price} className="font-display text-base font-bold text-navy" testid={`product-price-${product.product_id}`} />
                {product.compare_price > product.price && (
                  <span className="block text-[11px]">
                    <s className="text-slate-400">{fmtXof(product.compare_price)}</s>{" "}
                    <span className="font-semibold text-emerald-600">-{Math.round((1 - product.price / product.compare_price) * 100)}%</span>
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {quoteOnly ? (
              <Link to={`/produit/${product.product_id}`} data-testid={`quote-cta-${product.product_id}`} className={CTA}>
                Demander un devis
              </Link>
            ) : (
              <button data-testid={`add-to-cart-${product.product_id}`} onClick={() => add(product)} className={CTA}>
                <ShoppingCart size={13} /> {isService ? "Réserver" : isDev ? "Commander" : "Ajouter"}
              </button>
            )}
            <PromoButton product={product} compact />
          </div>
        </div>
        )}
      </div>
    </motion.article>
  );
}
